import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useId, useRef, useState } from 'react';

import type { CanvassingSessionFetchImpl } from './canvassingSessionApi';
import { buildCanvassingSessionFilterVariables } from './canvassingSessionApi';
import type { CanvassingSessionFilterState } from './canvassingSessionFilters';
import { describeCanvassingSessionFilters } from './canvassingSessionFilterSummary';
import { useBulkCanvassingSessionTranscription } from './useBulkCanvassingSessionTranscription';

const CONFIRMATION_BODY =
  'All sessions matching these filters, across all pages. Completed, in-progress, and unavailable recordings are skipped. Submissions are spaced 15 seconds apart. Unsent work is lost on server restart.';

type ConfirmationSnapshot = {
  filter: ReturnType<typeof buildCanvassingSessionFilterVariables>;
  listTotal: number;
  summary: string;
};

export type CanvassingSessionBulkTranscriptionProps = {
  filters: CanvassingSessionFilterState;
  listTotal: number;
  disabled: boolean;
  fetchImpl?: CanvassingSessionFetchImpl;
  resolveCanvasserLabel: (canvasserId: string) => string;
};

export const CanvassingSessionBulkTranscription = ({
  filters,
  listTotal,
  disabled,
  fetchImpl,
  resolveCanvasserLabel,
}: CanvassingSessionBulkTranscriptionProps) => {
  const id = useId();
  const bulk = useBulkCanvassingSessionTranscription({ fetchImpl });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snapshot, setSnapshot] = useState<ConfirmationSnapshot | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const actionRegionRef = useRef<HTMLDivElement | null>(null);
  const restoreFocusRef = useRef(false);
  const dialogOpenRef = useRef(false);
  const submissionLockRef = useRef(false);
  const pending = bulk.state.status === 'pending';
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;
  const summaryId = `${id}-summary`;

  const openConfirmation = (): void => {
    if (disabled || pending) return;
    const filter = buildCanvassingSessionFilterVariables(filters);
    setSnapshot({
      filter,
      listTotal,
      summary: describeCanvassingSessionFilters(filters, resolveCanvasserLabel),
    });
    dialogOpenRef.current = true;
    setDialogOpen(true);
  };

  const closeConfirmation = (): void => {
    restoreFocusRef.current = true;
    dialogOpenRef.current = false;
    setDialogOpen(false);
  };

  const confirm = async (): Promise<void> => {
    if (snapshot === null || submissionLockRef.current || pending) return;
    submissionLockRef.current = true;
    try {
      await bulk.start(snapshot.filter);
    } finally {
      submissionLockRef.current = false;
      if (dialogOpenRef.current) closeConfirmation();
    }
  };

  return (
    <>
      <Box
        ref={actionRegionRef}
        role="group"
        aria-label="Session bulk processing action"
        tabIndex={-1}
        sx={{ width: { xs: '100%', sm: 'auto' } }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignItems: { sm: 'center' }, flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={openConfirmation} disabled={disabled || pending} aria-busy={pending} ref={triggerRef}>
            {pending ? 'Starting bulk processing…' : 'Process matching sessions in Core'}
          </Button>
          {bulk.state.status === 'started' && (
            <Typography role="status" aria-live="polite" variant="body2">
              Bulk processing started. Eligible sessions will be sent to Core at 15-second intervals. Results appear on session detail pages.
            </Typography>
          )}
          {bulk.state.status === 'no-matches' && (
            <Typography role="status" aria-live="polite" variant="body2">No sessions match these filters.</Typography>
          )}
          {bulk.state.status === 'error' && (
            <Alert severity={bulk.state.uncertain ? 'warning' : 'error'} role="alert" aria-live="assertive">
              {bulk.state.message}
            </Alert>
          )}
        </Stack>
      </Box>
      <Dialog
        open={dialogOpen}
        onClose={closeConfirmation}
        aria-labelledby={titleId}
        aria-describedby={`${descriptionId} ${summaryId}`}
        aria-busy={pending}
        slotProps={{
          paper: { 'aria-busy': pending },
          transition: {
            onExited: () => {
              if (!restoreFocusRef.current) return;
              restoreFocusRef.current = false;
              if (triggerRef.current?.disabled) actionRegionRef.current?.focus();
              else triggerRef.current?.focus();
            },
          },
        }}
      >
        <DialogTitle id={titleId}>Process matching sessions in Core?</DialogTitle>
        <DialogContent>
          <DialogContentText id={descriptionId}>{CONFIRMATION_BODY}</DialogContentText>
          {snapshot !== null && (
            <DialogContentText id={summaryId} sx={{ mt: 1 }}>
              Matching list total: {snapshot.listTotal}. {snapshot.summary} This total is informational and does not guarantee eligibility.
            </DialogContentText>
          )}
          {pending && (
            <Stack role="status" aria-live="polite" direction="row" spacing={1} sx={{ alignItems: 'center', mt: 2 }}>
              <CircularProgress size={20} aria-hidden />
              <Typography variant="body2">Starting bulk processing…</Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={closeConfirmation}>Cancel</Button>
          <Button variant="contained" onClick={() => void confirm()} disabled={pending || snapshot === null}>
            {pending ? 'Starting…' : 'Start processing'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
